import { useCallback, useEffect, useMemo, useState } from 'react';
import SimpleModal from '../SimpleModal';
import { bulkAddRemovePrefixSuffix } from '../../api/channelBulkAdvanced';
import { fetchChannelsIndex, type ChannelRow } from '../../api/channels';
import { getApiErrorMessage } from '../../api/http';
import { transformChannelName } from '../../lib/prefixSuffixName';
import { useToast } from '../../context/ToastContext';
import { useUndoRedo } from '../../context/UndoRedoContext';
import { useBasicEditorToolContext } from '../../context/EditorSessionContext';
import { usePlaylist } from '../../context/PlaylistContext';

function normCat(c: string | null | undefined) {
  if (c == null || c === '') return 'UNCATEGORIZED';
  const v = String(c).trim().toUpperCase();
  if (v === 'UNCATEGORIZED') return 'UNCATEGORIZED';
  return v;
}

export default function PrefixSuffixModal({ show, onClose }: { show: boolean; onClose: () => void }) {
  const { showToast } = useToast();
  const { record, refreshEditor } = useUndoRedo();
  const { selectedIds, gridActive } = useBasicEditorToolContext();
  const { playlistId } = usePlaylist();

  const [channels, setChannels] = useState<ChannelRow[]>([]);
  const [position, setPosition] = useState<'prefix' | 'suffix'>('prefix');
  const [removePrefix, setRemovePrefix] = useState(false);
  const [removeSuffix, setRemoveSuffix] = useState(false);
  const [text, setText] = useState('');
  const [scope, setScope] = useState<'all' | 'selected' | 'categories'>('all');
  const [catPick, setCatPick] = useState<Record<string, boolean>>({});
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [busy, setBusy] = useState(false);

  const categories = useMemo(() => {
    const s = new Set<string>();
    for (const c of channels) s.add(normCat(c.category));
    return [...s].sort((a, b) => a.localeCompare(b, 'en'));
  }, [channels]);

  const load = useCallback(async () => {
    if (!playlistId) return;
    try {
      const rows = await fetchChannelsIndex(playlistId);
      setChannels(rows);
      const init: Record<string, boolean> = {};
      for (const c of rows) init[normCat(c.category)] = true;
      setCatPick(init);
    } catch (e) {
      showToast(getApiErrorMessage(e, 'Failed to fetch channels.'), 'error');
    }
  }, [playlistId, showToast]);

  useEffect(() => {
    if (!show) return;
    setPosition('prefix');
    setRemovePrefix(false);
    setRemoveSuffix(false);
    setText('');
    setCurrentStep(1);
    if (selectedIds.length > 0 && gridActive) setScope('selected');
    else setScope('all');
    void load();
  }, [show, load, selectedIds.length, gridActive]);

  const targetRows = useCallback((): ChannelRow[] => {
    if (scope === 'selected') {
      const set = new Set(selectedIds.map(Number));
      return channels.filter((ch) => set.has(Number(ch.id)));
    }
    if (scope === 'categories') {
      const cats = Object.entries(catPick)
        .filter(([, v]) => v)
        .map(([k]) => k);
      const cset = new Set(cats);
      return channels.filter((ch) => cset.has(normCat(ch.category)));
    }
    return channels;
  }, [channels, scope, selectedIds, catPick]);

  const selectedCategories = useMemo(
    () =>
      Object.entries(catPick)
        .filter(([, v]) => v)
        .map(([k]) => k),
    [catPick],
  );

  const effectiveRemove = position === 'prefix' ? removePrefix : removeSuffix;
  const txtTrim = text.trim();
  const target = useMemo(() => targetRows(), [targetRows]);

  const demoTo = useMemo(() => {
    if (!txtTrim) return 'Demo name';
    return transformChannelName('Demo name', position, effectiveRemove, txtTrim);
  }, [txtTrim, position, effectiveRemove]);

  const preview = useMemo(() => {
    if (!txtTrim) return { totalFiltered: target.length, totalWillChange: 0, sample: [] as Array<{ oldName: string; newName: string }> };
    const sample: Array<{ oldName: string; newName: string }> = [];
    let totalWillChange = 0;
    for (const ch of target) {
      const oldName = String(ch.name ?? '');
      const newName = transformChannelName(oldName, position, effectiveRemove, txtTrim);
      if (newName !== oldName) {
        totalWillChange++;
        if (sample.length < 6) sample.push({ oldName, newName });
      }
    }
    return { totalFiltered: target.length, totalWillChange, sample };
  }, [target, txtTrim, position, effectiveRemove]);

  function canGoStep2(): boolean {
    if (!txtTrim) {
      showToast('Enter the prefix/suffix text.', 'warning');
      return false;
    }
    return true;
  }

  async function runApply() {
    if (!playlistId) {
      showToast('Select a playlist (e.g. from Playlist manager).', 'warning');
      return;
    }
    const pid = parseInt(playlistId, 10);
    if (!txtTrim) {
      showToast('Enter prefix/suffix text.', 'warning');
      return;
    }
    let channel_ids: number[] = [];
    let categoriesScope: string[] = [];
    if (scope === 'selected') {
      if (!gridActive || selectedIds.length === 0) {
        showToast('Select channels in the Basic editor.', 'warning');
        return;
      }
      channel_ids = selectedIds.map(Number);
    } else if (scope === 'categories') {
      categoriesScope = selectedCategories;
      if (categoriesScope.length === 0) {
        showToast('Select at least one category.', 'warning');
        return;
      }
    }

    const rows = targetRows();
    const undoValuesById: Record<string, string> = {};
    const redoValuesById: Record<string, string> = {};
    for (const ch of rows) {
      const id = Number(ch.id);
      if (!Number.isFinite(id)) continue;
      const oldName = String(ch.name ?? '');
      const newName = transformChannelName(oldName, position, effectiveRemove, txtTrim);
      if (newName === oldName) continue;
      undoValuesById[id] = oldName;
      redoValuesById[id] = newName;
    }

    if (Object.keys(redoValuesById).length === 0) {
      showToast('No changes to apply.', 'warning');
      return;
    }

    setBusy(true);
    try {
      const res = await bulkAddRemovePrefixSuffix({
        playlist_id: pid,
        target: position,
        text: txtTrim,
        remove: effectiveRemove,
        scope,
        channel_ids,
        categories: categoriesScope,
      });
      if (!res.success) {
        showToast(res.message || 'Operation failed.', 'error');
        return;
      }
      showToast(res.message || 'Updated.', 'success');
      record({
        label: 'Prefix/Suffix editor',
        undo: { type: 'setField', playlist_id: pid, field: 'name', valuesById: undoValuesById },
        redo: { type: 'setField', playlist_id: pid, field: 'name', valuesById: redoValuesById },
      });
      onClose();
      await refreshEditor();
    } catch (e) {
      showToast(getApiErrorMessage(e, 'Server connection error.'), 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <SimpleModal show={show} onClose={onClose} title="Add/remove a prefix or a suffix" footer={null} size="lg" modalId="prefixSuffixWizardModal">
        <div className="wizard-container">
          <div data-color="primary" className="card card-wizard active mb-0">
            <div className="text-center card-header" data-background-color="primary">
              <h3 className="card-title">Add/remove a prefix or a suffix</h3>
              <h3 className="description"> </h3>
              <div className="wizard-navigation">
                <div className="progress-with-circle">
                  <div className="progress-bar" role="progressbar" style={{ width: currentStep === 1 ? '0%' : '50%' }} />
                </div>
                <ul className="nav nav-pills">
                  <li className="nav-item" style={{ width: '50%' }}>
                    <a className={`nav-link ${currentStep >= 1 ? 'active checked' : ''}`}>
                      <i className="fas fa-cog" />
                      <p>Config</p>
                    </a>
                  </li>
                  <li className="nav-item" style={{ width: '50%' }}>
                    <a className={`nav-link ${currentStep >= 2 ? 'active checked' : ''}`}>
                      <i className="fas fa-dna" />
                      <p>Data</p>
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            <div className="card-body">
              <div className="tab-content">
                <div className={`tab-pane fade ${currentStep === 1 ? 'show active' : ''}`}>
                  <label>Add a prefix or suffix to streams.</label>
                  <br />
                  <label>
                    <span>Demo name</span>
                    &nbsp;-&gt;&nbsp;
                    <span>{demoTo}</span>
                  </label>

                  <div className="mt-3">
                    <div className="custom-control custom-radio">
                      <input
                        type="radio"
                        className="custom-control-input"
                        name="psPosition"
                        id="psPrefix"
                        checked={position === 'prefix'}
                        onChange={() => setPosition('prefix')}
                      />
                      <label className="custom-control-label" htmlFor="psPrefix">
                        <b>Prefix: </b>Beginning of the name
                      </label>
                    </div>
                    <div className="custom-control custom-radio mt-2">
                      <input
                        type="radio"
                        className="custom-control-input"
                        name="psPosition"
                        id="psSuffix"
                        checked={position === 'suffix'}
                        onChange={() => setPosition('suffix')}
                      />
                      <label className="custom-control-label" htmlFor="psSuffix">
                        <b>Suffix: </b>End of the name
                      </label>
                    </div>
                  </div>

                  <div className="mt-3">
                    <div className="custom-control custom-checkbox">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        id="psRemovePrefix"
                        checked={removePrefix}
                        onChange={(e) => setRemovePrefix(e.target.checked)}
                      />
                      <label className="custom-control-label" htmlFor="psRemovePrefix">
                        <span className="text-muted">Remove prefix</span>
                      </label>
                    </div>
                    <div className="custom-control custom-checkbox mt-2" style={{ display: position === 'suffix' ? 'block' : 'none' }}>
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        id="psRemoveSuffix"
                        checked={removeSuffix}
                        onChange={(e) => setRemoveSuffix(e.target.checked)}
                      />
                      <label className="custom-control-label" htmlFor="psRemoveSuffix">
                        <span className="text-muted">Remove suffix</span>
                      </label>
                    </div>
                  </div>

                  <div className="mt-3">
                    <input
                      id="psText"
                      placeholder="US:"
                      type="text"
                      className="form-control"
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                    />
                  </div>
                </div>

                <div className={`tab-pane fade ${currentStep === 2 ? 'show active' : ''}`}>
                  <div className="mt-2 small text-muted">
                    {txtTrim
                      ? `Preview: ${preview.totalWillChange} channel(s) will be updated (out of ${preview.totalFiltered} in scope).`
                      : 'Enter text to see the preview.'}
                  </div>
                  <div className="mb-2">
                    {txtTrim && preview.totalWillChange === 0 ? (
                      <div className="text-muted small">No matches for the given change.</div>
                    ) : (
                      preview.sample.map((item, idx) => (
                        <div className="small mb-1" key={`${item.oldName}-${item.newName}-${idx}`}>
                          <span className="text-muted">-</span> <code>{item.oldName}</code> -&gt; <code>{item.newName}</code>
                        </div>
                      ))
                    )}
                  </div>

                  <div className="custom-control custom-radio">
                    <input
                      type="radio"
                      className="custom-control-input"
                      name="psScope"
                      id="psScopeSelected"
                      checked={scope === 'selected'}
                      disabled={!gridActive || selectedIds.length === 0}
                      onChange={() => setScope('selected')}
                    />
                    <label className="custom-control-label" htmlFor="psScopeSelected">
                      {selectedIds.length} selected streams
                    </label>
                  </div>

                  <div className="custom-control custom-radio mt-2">
                    <input
                      type="radio"
                      className="custom-control-input"
                      name="psScope"
                      id="psScopeCategories"
                      checked={scope === 'categories'}
                      onChange={() => setScope('categories')}
                    />
                    <label className="custom-control-label" htmlFor="psScopeCategories">
                      Streams from categories
                    </label>
                  </div>

                  <div className="custom-control custom-radio mt-2">
                    <input
                      type="radio"
                      className="custom-control-input"
                      name="psScope"
                      id="psScopeAll"
                      checked={scope === 'all'}
                      onChange={() => setScope('all')}
                    />
                    <label className="custom-control-label" htmlFor="psScopeAll">
                      All streams
                    </label>
                  </div>

                  <div id="psCategoriesWrap" className="mt-3" style={{ display: scope === 'categories' ? 'block' : 'none' }}>
                    <div className="small text-muted mb-2">Select categories:</div>
                    <div id="psCategoriesList" className="d-flex flex-column gap-1">
                      {categories.map((c) => {
                        const display = c.toUpperCase();
                        return (
                          <div className="form-check mb-1" key={c}>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`ps-cat-${c}`}
                              checked={!!catPick[c]}
                              onChange={(e) => setCatPick((p) => ({ ...p, [c]: e.target.checked }))}
                            />
                            <label className="form-check-label small" htmlFor={`ps-cat-${c}`}>
                              {display}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card-footer">
              <div style={{ float: 'left' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ display: currentStep === 2 ? 'inline-block' : 'none' }}
                  onClick={() => setCurrentStep(1)}
                >
                  Previous
                </button>
              </div>
              <div style={{ float: 'right' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ display: currentStep === 1 ? 'inline-block' : 'none' }}
                  onClick={() => {
                    if (!canGoStep2()) return;
                    setCurrentStep(2);
                  }}
                >
                  Next
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  disabled={busy}
                  style={{ display: currentStep === 2 ? 'inline-block' : 'none' }}
                  onClick={() => void runApply()}
                >
                  {busy ? <span className="spinner-border spinner-border-sm" /> : 'Apply'}
                </button>
              </div>
              <div className="clearfix" />
            </div>
          </div>
        </div>
      </SimpleModal>
    </>
  );
}
